import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, Send, Share2, Check, Copy } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { quantities } from "./cadGeometry";
import { swatchUrl } from "./tileTexture";
import { INPUT } from "./StudioPanels";

const BASE = process.env.REACT_APP_BACKEND_URL;
const gbp = (n) => `£${(n || 0).toLocaleString("en-GB", { maximumFractionDigits: 0 })}`;

const Board = ({ q }) => (
  <div className="grid grid-cols-3 sm:grid-cols-4 gap-2" data-testid="moodboard-swatches">
    {[...q.tiles, ...q.panels].map((t, i) => (
      <div key={t.id} className={`relative rounded-lg overflow-hidden ${i === 0 ? "col-span-2 row-span-2" : ""}`}>
        {t.swatch ? <img src={swatchUrl(t.swatch)} alt={t.name} className="w-full h-full object-cover aspect-square" /> : <div className="aspect-square" style={{ background: t.colour }} />}
        <span className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/75 to-transparent text-[11px] text-white px-2 pt-4 pb-1.5">{t.name}</span>
      </div>
    ))}
  </div>
);

const Lines = ({ q }) => (
  <div className="text-sm" data-testid="moodboard-product-list">
    {q.tiles.map((t) => (
      <div key={t.id} className="flex justify-between gap-3 py-2 border-b border-white/5"><span className="text-rossi-cream">{t.name} <span className="text-xs text-rossi-stone font-mono">{t.area.toFixed(1)} m² → order {t.order} m²</span></span><span className="font-mono text-rossi-gold">{gbp(t.est)}</span></div>
    ))}
    {q.panels.map((p) => (
      <div key={p.id} className="flex justify-between gap-3 py-2 border-b border-white/5"><span className="text-rossi-cream">{p.name} splashback <span className="text-xs text-rossi-stone font-mono">× {p.qty} panel{p.qty > 1 ? "s" : ""}</span></span><span className="font-mono text-rossi-gold">{gbp(p.est)}</span></div>
    ))}
    {q.fixtures.map((f) => (
      <div key={f.uid} className="flex justify-between gap-3 py-2 border-b border-white/5"><span className="text-rossi-cream/90">{f.meta.name}</span><span className="font-mono text-rossi-gold">{f.meta.price_note || gbp(f.meta.price_gbp)}</span></div>
    ))}
    {q.tap && <div className="flex justify-between gap-3 py-2 border-b border-white/5"><span className="text-rossi-cream/90">{q.tap.name} <span className="text-xs text-rossi-stone font-mono">× {q.tap.qty}</span></span><span className="font-mono text-rossi-gold">{gbp(q.tap.est)}</span></div>}
    <div className="flex justify-between pt-3"><span className="text-rossi-stone">Guide materials estimate</span><span className="font-mono text-rossi-gold text-lg" data-testid="moodboard-total">{gbp(q.total)}</span></div>
    <p className="text-[11px] text-rossi-stone mt-1">Floor {q.floor.toFixed(2)} m² · tiled {q.tiled.toFixed(2)} m² incl. 10% cutting allowance. Installation is quoted after a free survey.</p>
  </div>
);

export const Moodboard = ({ state, collections, meta, brassware, getSnapshot }) => {
  const q = quantities(state, collections, meta, brassware);
  const [lead, setLead] = useState({ name: "", email: "", phone: "", postcode: "" });
  const [busy, setBusy] = useState(null);
  const [saved, setSaved] = useState(null);
  const save = async (send) => {
    if (send && (!lead.name || !lead.email)) { toast.error("Please add your name and email."); return; }
    setBusy(send ? "send" : "share");
    try {
      const snapshot = await getSnapshot();
      const { data } = await api.post("/cad/designs", { state, snapshot, send, public_base: BASE, ...(send ? lead : {}) });
      setSaved({ ...data, sent: send });
      toast.success(send ? "Your design has been sent to our design team" : "Design saved — share link ready");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save your design. Please try again."); }
    finally { setBusy(null); }
  };
  const link = saved ? `${window.location.origin}/design/${saved.id}` : "";
  return (
    <div className="grid lg:grid-cols-[1fr_1fr] gap-6 mt-8" data-testid="cad-moodboard">
      <div className="card-lux p-6">
        <p className="eyebrow mb-3">Moodboard</p>
        {q.tiles.length + q.panels.length ? <Board q={q} /> : <p className="text-sm text-rossi-stone">Choose a tile for a surface to start your moodboard.</p>}
      </div>
      <div className="card-lux p-6">
        <p className="eyebrow mb-3">Product list</p>
        <Lines q={q} />
        {saved?.sent ? (
          <div className="mt-6 rounded-xl border border-rossi-gold/30 bg-rossi-gold/5 p-4 text-sm text-rossi-cream" data-testid="cad-sent"><Check size={16} className="inline text-rossi-gold mr-1" /> Sent to our design team — we’ll be in touch shortly. <Link to="/booking" className="text-rossi-gold underline ml-1">Book a consultation</Link></div>
        ) : (
          <div className="mt-6 space-y-3">
            <div className="grid grid-cols-2 gap-2">
              {["name", "email", "phone", "postcode"].map((k) => <input key={k} placeholder={k[0].toUpperCase() + k.slice(1)} value={lead[k]} onChange={(e) => setLead({ ...lead, [k]: e.target.value })} className={INPUT} data-testid={`cad-lead-${k}`} />)}
            </div>
            <div className="flex flex-wrap gap-2">
              <button onClick={() => save(true)} disabled={!!busy} className="btn-gold disabled:opacity-50" data-testid="cad-send">{busy === "send" ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />} Send to Rossi</button>
              <button onClick={() => save(false)} disabled={!!busy} className="btn-ghost-gold disabled:opacity-50" data-testid="cad-share">{busy === "share" ? <Loader2 size={16} className="animate-spin" /> : <Share2 size={16} />} Save &amp; share</button>
            </div>
          </div>
        )}
        {saved && (
          <div className="mt-4 flex gap-2 items-center" data-testid="cad-share-link">
            <input readOnly value={link} className={INPUT} />
            <button onClick={() => { navigator.clipboard?.writeText(link); toast.success("Link copied"); }} className="btn-ghost-gold !px-3" data-testid="cad-copy-link"><Copy size={15} /></button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Moodboard;
